/**
 * Pack hour + layout into GPU sky uniforms. Pure JS — no Pixi, no canvas.
 * The fragment shader samples the same palettes/orbit as the 2D sky scene.
 */

import { seededHash01 } from "../../core/day-night.js";
import { sunAlpha } from "./celestial.js";
import { CLOUD_COUNT, layoutSkyClouds } from "./clouds.js";
import { starFieldAlpha } from "./stars.js";
import { nightAmount } from "./sky-utils.js";
import {
  buildSkySceneContext,
  cloudDayBoundary,
  cloudWarm,
} from "../sky-scene.js";

export var SKY_SHADER_CLOUD_MAX = CLOUD_COUNT;

var SOLID_DAY_RGB = [207 / 255, 239 / 255, 251 / 255];

function rgb01(c) {
  if (!c || !c.length) return [1, 1, 1];
  return [c[0] / 255, c[1] / 255, c[2] / 255];
}

/**
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} hour
 * @param {{
 *   layout?: object,
 *   runSeed?: number,
 *   drawStars?: boolean,
 *   drawClouds?: boolean,
 *   drawCelestial?: boolean,
 * }} [opts]
 */
export function buildSkyShaderUniforms(bounds, hour, opts) {
  opts = opts || {};
  var scene = buildSkySceneContext(bounds, hour, opts);
  if (!scene) {
    return { solid: true, solidRgb: SOLID_DAY_RGB.slice() };
  }

  var state = scene.state;
  var t = scene.t;
  var night = nightAmount(t);
  var showCelestial = opts.drawCelestial !== false;
  var showClouds = opts.drawClouds !== false;
  var starsOn = opts.drawStars !== false;
  var starAlpha = starsOn ? starFieldAlpha(t) : 0;
  if (scene.skyBand.height <= 0) starAlpha = 0;

  var warm = cloudWarm(state.phase);
  var play = scene.layout.playArea || scene.layout.coreRect || bounds;
  var cloudBandBottomY = play.y + play.height * 0.10;
  var clouds = showClouds
    ? layoutSkyClouds(bounds, scene.animMs, scene.cloudSeed, {
      layout: scene.layout,
      cloudBandBottomY: cloudBandBottomY,
    })
    : [];

  var packed = [];
  var i;
  for (i = 0; i < SKY_SHADER_CLOUD_MAX; i++) {
    var c = clouds[i];
    if (!c) {
      packed.push({ ox: 0, oy: 0, bodyW: 0, bodyH: 0, alpha: 0, bumpA: { x: 0, y: 0, r: 0 }, bumpB: { x: 0, y: 0, r: 0 } });
      continue;
    }
    var h6 = seededHash01(c.seed, 6.37);
    var alpha = 0.82 + h6 * 0.13;
    alpha += (1 - alpha) * night;
    packed.push({
      ox: c.ox,
      oy: c.oy,
      bodyW: c.bodyW,
      bodyH: c.bodyH,
      alpha: alpha,
      bumpA: c.bumpA,
      bumpB: c.bumpB,
    });
  }

  var discVisible = showCelestial && scene.discVisible ? 1 : 0;
  var emergence = scene.emergence;
  var cloudLight = discVisible
    ? {
      x: scene.pos.x,
      y: scene.pos.y,
      glowRadius: scene.bodyRadius * 8,
      color: state.isDay ? [255, 232, 158] : [200, 220, 255],
      strength: (state.isDay ? 1.05 : 0.5) * (0.3 + 0.7 * emergence),
    }
    : { x: 0, y: 0, glowRadius: 0, color: [0, 0, 0], strength: 0 };

  return {
    solid: false,
    solidRgb: SOLID_DAY_RGB.slice(),
    width: bounds.width,
    height: bounds.height,
    colorPhase: t,
    seaLineY: scene.seaLineY,
    bodyX: scene.pos.x,
    bodyY: scene.pos.y,
    bodyRadius: scene.bodyRadius,
    emergence: emergence,
    isDay: state.isDay ? 1 : 0,
    discVisible: discVisible,
    sunAlpha: state.isDay ? sunAlpha(t) : 1,
    animSec: scene.animSec,
    seed: scene.runSeed || 0,
    scaleUi: scene.scaleUi,
    starAlpha: starAlpha,
    drawClouds: showClouds ? 1 : 0,
    drawCelestial: showCelestial ? 1 : 0,
    cloudBoundary: cloudDayBoundary(state.phase),
    cloudWarmth: warm.amount,
    nightAmount: night,
    cloudCount: Math.min(SKY_SHADER_CLOUD_MAX, clouds.length),
    warmTop: rgb01(warm.colors.top),
    warmMid: rgb01(warm.colors.mid),
    warmBot: rgb01(warm.colors.bot),
    lightX: cloudLight.x,
    lightY: cloudLight.y,
    lightRadius: cloudLight.glowRadius,
    lightStrength: cloudLight.strength,
    lightRgb: rgb01(cloudLight.color),
    clouds: packed,
  };
}

export function skyShaderSolidUniforms() {
  return { solid: true, solidRgb: SOLID_DAY_RGB.slice() };
}
